import { notifyHostClose } from "@do/shared-types";
import { Button, type ButtonProps } from "@do/ui/components/button";
import { Dialog, DialogTrigger } from "@do/ui/components/dialog";
import { cn } from "@do/ui/lib/utils";
import { ChevronDownIcon, RefreshCcw, Settings2Icon } from "lucide-react";
import { useTranslations } from "next-intl";
import { useCallback, useMemo } from "react";
import { useChat } from "../../providers/chat-provider";
import { HeaderSettings } from "./settings";
import { HeaderSettingsNoCssFallback } from "./settings/no-css-fallback";

const HeaderAction = ({
  children,
  className,
  ...props
}: { children: React.ReactNode } & ButtonProps) => {
  return (
    <Button
      variant="ghost"
      size="icon"
      className={cn(
        "size-fit rounded-(--header-actions-border-radius) p-(--header-actions-padding) bg-(--header-actions-background)/(--header-actions-background-opacity) hover:bg-(--header-actions-hover-background)/(--header-actions-hover-background-opacity) text-(--header-actions-foreground)/(--header-actions-foreground-opacity) hover:text-(--header-actions-hover-foreground)/(--header-actions-hover-foreground-opacity)",
        className
      )}
      {...props}
    >
      {children}
    </Button>
  );
};

export const HeaderActions = ({
  dialogPortalContainer,
  forceOpenSettings,
}: {
  forceOpenSettings?: boolean;
  dialogPortalContainer?: HTMLElement | null;
}) => {
  const {
    isTestingMode,
    currentLocale,
    selectedLocale,
    defaultLocale,
    chatbotParams,
    onClose,
    onRefreshConversation,
    isClosable = false,
  } = useChat();
  const t = useTranslations("do-chat-v2");

  const handleClose = useCallback(() => {
    onClose ? onClose() : notifyHostClose();
  }, [onClose]);

  const showCloseButton = useMemo(
    () => (isTestingMode ? false : (isClosable || chatbotParams.enableCloseButton) ?? false),
    [isTestingMode, chatbotParams.enableCloseButton, isClosable]
  );
  const showRefreshSection = useMemo(
    () => (isTestingMode ? true : chatbotParams.enableRefreshButton ?? false),
    [isTestingMode, chatbotParams.enableRefreshButton]
  );
  const showLanguageSection = useMemo(
    () => !!(Object.keys(chatbotParams.availableLocales || {}).length > 1 && currentLocale),
    [chatbotParams.availableLocales, currentLocale]
  );
  const showLegalSection = useMemo(
    () =>
      !!(
        (selectedLocale?.disclaimerText ||
          defaultLocale?.disclaimerText ||
          chatbotParams.disclaimerText) &&
        chatbotParams.disclaimerMessagePlacement === "settings"
      ),
    [
      selectedLocale?.disclaimerText,
      defaultLocale?.disclaimerText,
      chatbotParams.disclaimerText,
      chatbotParams.disclaimerMessagePlacement,
    ]
  );

  return (
    <div id="header-actions" className="flex items-center gap-2">
      {isTestingMode && (
        <HeaderAction
          id="header-actions-refresh"
          onClick={onRefreshConversation}
          aria-label={t("chat.header.accessibility.refresh-chatbot-aria-label")}
        >
          <span className="sr-only">
            {t("chat.header.accessibility.refresh-chatbot-aria-label")}
          </span>
          <RefreshCcw
            id="header-actions-refresh-icon"
            aria-hidden="true"
            className="size-(--header-actions-icon-size)"
          />
        </HeaderAction>
      )}

      {(showRefreshSection || showLanguageSection || showLegalSection) && (
        <>
          <HeaderSettingsNoCssFallback
            showRefreshSection={showRefreshSection}
            showLanguageSection={showLanguageSection}
            showLegalSection={showLegalSection}
          />

          <Dialog open={forceOpenSettings} modal={dialogPortalContainer === undefined}>
            <DialogTrigger asChild>
              <HeaderAction
                id="header-actions-settings"
                aria-label={t("chat.header.accessibility.open-settings-aria-label")}
              >
                <span className="sr-only">
                  {t("chat.header.accessibility.open-settings-aria-label")}
                </span>
                <Settings2Icon
                  id="header-actions-settings-icon"
                  aria-hidden="true"
                  className="size-(--header-actions-icon-size)"
                />
              </HeaderAction>
            </DialogTrigger>

            <HeaderSettings
              showRefreshSection={showRefreshSection}
              showLanguageSection={showLanguageSection}
              showLegalSection={showLegalSection}
              dialogPortalContainer={dialogPortalContainer}
            />
          </Dialog>
        </>
      )}

      {showCloseButton && (
        <HeaderAction
          id="header-actions-collapse"
          onClick={handleClose}
          aria-label={t("chat.header.accessibility.close-chatbot-aria-label")}
        >
          <span className="sr-only">{t("chat.header.accessibility.close-chatbot-aria-label")}</span>
          <ChevronDownIcon
            id="header-actions-collapse-icon"
            aria-hidden="true"
            className="size-(--header-actions-icon-size)"
          />
        </HeaderAction>
      )}
    </div>
  );
};
